export function getProductGroupValues(shopConfig = {}) {
  const description = (shopConfig?.subheadline || '').toString().trim();
  const fallbackName = (shopConfig?.type || shopConfig?.headline || '').toString().trim();
  const fallbackGroupId = fallbackName || (shopConfig?.headline || '').toString().trim();
  const imageUrl = (shopConfig?.pictureurl || shopConfig?.thumbnailurl || '').toString().trim();
  const groupIds = (shopConfig?.gaItemId || '')
    .toString()
    .split(',')
    .map((id) => id.trim())
    .filter(Boolean);

  const uniqueGroupIds = [...new Set(groupIds)];

  if (uniqueGroupIds.length > 0) {
    return {
      name: uniqueGroupIds[0],
      description,
      imageUrl,
      groupId: uniqueGroupIds[0],
      groupIds: uniqueGroupIds,
      isMultiGroup: uniqueGroupIds.length > 1,
    };
  }

  return {
    name: fallbackName,
    description,
    imageUrl,
    groupId: fallbackGroupId,
    groupIds: fallbackGroupId ? [fallbackGroupId] : [],
    isMultiGroup: false,
  };
}

export function normalizeVariantValue(value) {
  if (typeof value !== 'string') {
    return value;
  }

  return value.split(';')[0]?.trim();
}

export function normalizePrice(value) {
  const normalized = normalizeVariantValue(value);

  if (normalized === null || normalized === undefined || normalized === '') {
    return null;
  }

  const parsed = Number.parseFloat(String(normalized).replace(',', '.'));

  if (Number.isNaN(parsed)) {
    return null;
  }

  return parsed.toFixed(2);
}

export function toSchemaAvailability(deliveryCategory, deliveryDays) {
  const category = Number.parseInt(String(deliveryCategory), 10);
  const days = Number.parseInt(String(deliveryDays), 10);

  if (category === 1 || days === 0) {
    return 'https://schema.org/InStock';
  }

  if (category >= 2 && category <= 4) {
    return 'https://schema.org/MadeToOrder';
  }

  return category > 1 ? 'https://schema.org/MadeToOrder' : 'https://schema.org/InStock';
}

export function getVariantUrl(sku, baseUrl = window.location.href) {
  const currentUrl = new URL(baseUrl);

  currentUrl.searchParams.delete('itemId');
  currentUrl.searchParams.set('itemId', String(sku));

  return currentUrl.toString();
}

export function extractModelIdFromArticleInfo(container, sku) {
  if (!container || !sku) {
    return '';
  }

  const articleInfoInput = container.querySelector(`#${CSS.escape(sku)}.articleinfo`);
  const articleInfoRaw = articleInfoInput?.value || '';

  if (!articleInfoRaw) {
    return '';
  }

  const modelMatch = articleInfoRaw.match(/(?:Typ|Type)\s*:\s*([0-9]+(?:[.,][0-9]+)?)/i);

  return modelMatch?.[1]?.replace(',', '.').trim() || '';
}

export function extractVariantsFromContainer({
  container,
  currency,
  processedSkus,
  skippedVariants,
  baseUrl,
}) {
  if (!container) {
    return [];
  }

  const deliveryCategoryInputs = [
    ...container.querySelectorAll("input[type='hidden'][id^='DeliveryCategory_']"),
  ];
  const variants = [];

  deliveryCategoryInputs.forEach((deliveryCategoryInput) => {
    const sku = deliveryCategoryInput.id.replace('DeliveryCategory_', '').trim();

    if (!sku || processedSkus.has(sku)) {
      skippedVariants.push({
        reason: !sku ? 'missing-sku' : 'duplicate-sku',
        sourceId: deliveryCategoryInput.id,
      });
      return;
    }

    processedSkus.add(sku);

    const deliveryDaysInput = container.querySelector(`#DeliveryDays_${CSS.escape(sku)}`);
    const priceInput = container.querySelector(`#Price_${CSS.escape(sku)}`);

    const deliveryCategory = normalizeVariantValue(deliveryCategoryInput.value);
    const deliveryDays = normalizeVariantValue(deliveryDaysInput?.value || '');
    const price = normalizePrice(priceInput?.value || '');
    const modelId = extractModelIdFromArticleInfo(container, sku);

    if (!price) {
      skippedVariants.push({
        sku,
        reason: 'missing-or-invalid-price',
        rawPrice: priceInput?.value || null,
        deliveryCategory,
        deliveryDays,
      });
      return;
    }

    variants.push({
      '@type': 'Product',
      sku,
      _modelId: modelId,
      offers: {
        '@type': 'Offer',
        url: getVariantUrl(sku, baseUrl),
        priceCurrency: currency,
        price,
        availability: toSchemaAvailability(deliveryCategory, deliveryDays),
      },
    });
  });

  return variants;
}

export function buildProductStructuredDataPayload(productGroupValues, variants) {
  const { name, description, imageUrl, groupId, groupIds, isMultiGroup } = productGroupValues;

  if (!Array.isArray(variants) || variants.length === 0) {
    return null;
  }

  const toSchemaVariant = (variant, productLabel) => {
    const schemaVariant = { ...variant };
    const variantNameBase = (productLabel || '').toString().trim();

    if (variantNameBase) {
      schemaVariant.name = `${variantNameBase} - ${variant.sku}`;
    }

    if (imageUrl) {
      schemaVariant.image = [imageUrl];
    }

    delete schemaVariant._modelId;

    return schemaVariant;
  };

  if (!isMultiGroup) {
    if (!name) {
      return null;
    }

    return {
      '@context': 'https://schema.org',
      '@type': 'ProductGroup',
      name,
      description,
      productGroupID: groupId || name,
      hasVariant: variants.map((variant) => toSchemaVariant(variant, groupId || name)),
    };
  }

  const variantsByGroupId = new Map(groupIds.map((id) => [id, []]));
  const unassignedVariants = [];

  variants.forEach((variant) => {
    const modelId = (variant._modelId || '').trim();

    if (modelId && variantsByGroupId.has(modelId)) {
      variantsByGroupId.get(modelId).push(toSchemaVariant(variant, modelId));
    } else {
      unassignedVariants.push(toSchemaVariant(variant, groupIds[0] || name));
    }
  });

  if (unassignedVariants.length > 0 && groupIds.length > 0) {
    variantsByGroupId.get(groupIds[0]).push(...unassignedVariants);
  }

  const graph = groupIds
    .map((id) => ({
      '@type': 'ProductGroup',
      name: id,
      description,
      productGroupID: id,
      hasVariant: variantsByGroupId.get(id) || [],
    }))
    .filter((group) => group.hasVariant.length > 0);

  if (graph.length === 0) {
    return null;
  }

  return {
    '@context': 'https://schema.org',
    '@graph': graph,
  };
}
